import { ApplicationView, useController } from '@aha-app/mvc';
import React from 'react';
import { type WhiteboardSidePanelItem } from '../../sidepanel-types';
import classNames from 'classnames';
import SmartRecordsController from '../../controllers/SmartRecordsController';
import RecordCreate from './RecordCreate';
import RecordResults from './RecordResults';
import EmptyState from './EmptyState';
import styles from './smart-records-panel.module.css';
import convertAhaShapesIcon from '../../assets/icons/convertAhaShapesIcon.svg';
import { svgToIcon } from '../../utils/svg';
import Promo from './Promo';
import RecordProgress from './RecordProgress';
import { canConvertSmartRecords } from '../../objects/guards';

export const PROMO_LINK =
  '/suite/roadmaps-whiteboards?upgrade_location=roadmaps_whiteboards_side_panel_convert';

const TabComponent: React.FC = () => {
  const controller = useController(SmartRecordsController);

  if (!controller.state.smartRecordsPresent) {
    return null;
  }

  if (
    !canConvertSmartRecords() &&
    !window.currentAccount?.hasCrossProductPromotionsEnabled
  ) {
    return null;
  }

  return (
    <aha-sidepanel-item
      onClick={() => {
        controller.actionSetPanelVisible(true);
        controller.actionInitializePanel();
      }}
      data-testid='smart-records-pull-tab'
    >
      {svgToIcon(convertAhaShapesIcon)}
    </aha-sidepanel-item>
  );
};

const SmartRecordsTabComponent = ApplicationView(TabComponent);

// Currently this is used for converting smart records into Aha! records.
const PanelComponent: React.FC = () => {
  const controller = useController(SmartRecordsController);
  const { showResults, submitting } = controller.state;
  const { availableRecordTypes } = controller;

  let content = <RecordCreate />;
  if (availableRecordTypes.length === 0) {
    content = <EmptyState />;
  } else if (submitting) {
    content = <RecordProgress />;
  } else if (showResults) {
    content = <RecordResults />;
  } else if (controller.showPromo) {
    content = <Promo />;
  } else if (!canConvertSmartRecords()) {
    return null;
  }

  return (
    <>
      <div className='side-panel__header' style={{ padding: 20 }}>
        <div
          className='side-panel__title'
          style={{ flexDirection: 'column', alignItems: 'flex-start' }}
          onClick={() => {
            if (controller.showPromo) {
              window.open(PROMO_LINK, '_self');

              window.AhaAnalytics?.track(
                'Feature-7333230858416702915',
                'User clicks promotion',
                {
                  'Upgrade location': 'Whiteboards side panel convert',
                  'Promotion product': 'Roadmaps',
                  'Originating product': window.currentAccount?.primaryFlavor,
                }
              );
            }
          }}
        >
          <h3
            className={classNames('h-500', 'side-panel__title-text')}
            data-testid='smart-records-panel-title'
          >
            Convert Aha! shapes
            {controller.showPromo && (
              <aha-promo-try-pill
                font-sizing='large'
                pill-size='small'
                upgrade-location={PROMO_LINK.split('?upgrade_location=').pop()}
                style={{ marginLeft: 'var(--spacer-2)' }}
              />
            )}
          </h3>
          {!controller.showPromo && (
            <div style={{ fontSize: 'var(--font-size-300)' }}>
              Convert your shapes into Aha! records. Select one or more Aha!
              shapes below to convert.
            </div>
          )}
        </div>
      </div>
      <div className={styles.panelContent}>{content}</div>
    </>
  );
};

const SmartRecordsPanel = ApplicationView(PanelComponent);

export const smartRecordItem = (
  controller: SmartRecordsController
): WhiteboardSidePanelItem => ({
  id: 'ahaShapes',
  name: 'Aha! shapes',
  icon: svgToIcon(convertAhaShapesIcon, { width: 18 }),
  alwaysRendered: true,
  tabContent: <SmartRecordsTabComponent />,
  panel: <SmartRecordsPanel />,
  promoLink: () => (controller.showPromo ? PROMO_LINK : undefined),
  testId: 'smart-records-tab',
  onOpen: () => {
    controller.actionSetPanelVisible(true);
    controller.actionInitializePanel(true);
  },
  onClose: () => {
    controller.actionSetPanelVisible(false);
  },
});
